import { useContext } from "react";
import {
  actionModeSet,
  deleteItemSet,
  ITeacher,
  selectDeleteItem,
  selectTeachersActionMode,
  updateItemSet,
} from "features/Teachers";
import TableBody from "@mui/material/TableBody";
import EditIcon from "components/SVGs/Edit";
import DeleteIcon from "components/SVGs/Delete";
import TableRow from "@mui/material/TableRow";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { SvgIconProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import ShareIcon from "components/SVGs/Share";
import ConfirmMessage from "components/ConfirmMessage";
import { ActionType } from "features/common/Interfaces";
import { useAppDispatch, useAppSelector } from "app/hooks";
import useToast from "hooks/useToast";
import {
  useDeleteTeacherMutation,
  useSendCredentialsMutation,
} from "app/services/teachers";
import EditTeacher from "../EditTeacher";
import Password from "./Password";
import useDebounce from "hooks/useDebounce";
import { FireBaseContext } from "contexts/FireBase";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "rgb(255 255 255)",
    color: "rgb(62 77 91)",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "16px 20px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "& td, & th": {
    color: "rgba(105 123 140)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

type Props = {
  list: ITeacher[];
};

const TBody = ({ list }: Props) => {
  const { logEvent } = useContext(FireBaseContext);
  const toast = useToast();
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const actionMode = useAppSelector((state) => selectTeachersActionMode(state));
  const deleteItem = useAppSelector((state) => selectDeleteItem(state));
  const [sendCredentials] = useSendCredentialsMutation();
  const { starting } = useDebounce<React.MouseEvent<SVGSVGElement>>();

  const [deleteTeacher] = useDeleteTeacherMutation();
  const editTeacherHandler = (item: ITeacher) => {
    dispatch(updateItemSet(item));
    dispatch(actionModeSet(ActionType.update));
    logEvent("ClickOnEditTeacher");
  };
  const deleteTeacherHandler = (item: ITeacher) => {
    dispatch(actionModeSet(ActionType.delete));
    dispatch(deleteItemSet(item));
    logEvent("ClickOnDeleteTeacher");
  };
  const sharePasswordHandler = async (item: ITeacher) => {
    try {
      const data = await sendCredentials({
        _userId: item.id,
        _lang: i18n.dir() === "rtl" ? "AR" : "EN",
      }).unwrap();
      if (data._statusCode === 200) {
        toast({
          label: t("success"),
          message: `${t("Credentials Sent")} ${item.email}`,
          rtl: i18n.dir() === "rtl",
          type: "success",
          toastId: "classUpdated",
        });
      } else throw data;
    } catch (error) {
      console.log(error);
    }
    logEvent("ClickOnShareTeachersCredentials");
  };

  const actionList = (item: ITeacher) => {
    return {
      ...item,
      sharePassword: (
        <ShareIcon
          onClick={starting(() => sharePasswordHandler(item))}
          sx={{
            cursor: "pointer",
            "& path": {
              fill: "rgb(105, 123, 140)",
            },
            "&:hover path": {
              fill: "rgb(97, 194, 238)",
            },
            "&:active path": {
              fill: "rgb(105, 123, 140)",
            },
          }}
        />
      ),
      password: <Password item={item} />,
      edit: (
        <EditIcon
          onClick={() => editTeacherHandler(item)}
          sx={{
            cursor: "pointer",
            "& path": {
              fill: "rgb(105, 123, 140)",
            },
            "&:hover path": {
              fill: "rgb(97, 194, 238)",
            },
            "&:active path": {
              fill: "rgb(105, 123, 140)",
            },
          }}
        />
      ),
      delete: (
        <DeleteIcon
          onClick={() => deleteTeacherHandler(item)}
          sx={{
            cursor: "pointer",
            "& path": {
              fill: "rgb(105, 123, 140)",
            },
            "&:hover path": {
              fill: "rgb(97, 194, 238)",
            },
            "&:active path": {
              fill: "rgb(105, 123, 140)",
            },
          }}
        />
      ),
    };
  };

  const enhancedList = list.map((item) => actionList(item));

  const align = i18n.dir() === "rtl" ? "right" : "left";

  const deleteHandler = async (answer: "yes" | "no") => {
    if (answer === "yes") {
      try {
        if (deleteItem) {
          const data = await deleteTeacher(deleteItem.teacherId).unwrap();
          if (data?._statusCode === 200) {
            toast({
              label: t("success"),
              message: t("Teacher Deleted"),
              rtl: i18n.dir() === "rtl",
              type: "success",
              toastId: "classUpdated",
            });
          } else if (data?._statusCode === 400) {
            toast({
              label: t("error"),
              message: t("Cant' delete teacher"),
              rtl: i18n.dir() === "rtl",
              type: "error",
              toastId: "classUpdated",
            });
          } else throw data;
        }
      } catch (error) {
        console.log({ error });
      }
      logEvent("ClickOnConfirmDeleteTeacher");
    }
    dispatch(deleteItemSet(null));
    dispatch(actionModeSet(ActionType.list));
  };
  const deleteDescription = (
    <>
      <div>{t("Do you want to delete teacher")}</div>
      <div style={{ fontWeight: "bold" }}>{deleteItem?.name}</div>
    </>
  );
  return (
    <>
      <ConfirmMessage
        open={ActionType.delete === actionMode}
        onClose={deleteHandler}
        description={deleteDescription}
        title={t("Delete Teacher")}
      />
      <EditTeacher
        open={actionMode === ActionType.update}
        onClose={() => dispatch(actionModeSet(ActionType.list))}
      />
      <TableBody>
        {enhancedList.map(
          (
            row: ITeacher & {
              sharePassword: React.ReactElement<SvgIconProps>;
              password: React.ReactElement<SvgIconProps>;
              edit: React.ReactElement<SvgIconProps>;
              delete: React.ReactElement<SvgIconProps>;
            }
          ) => (
            <StyledTableRow key={row.id}>
              <StyledTableCell
                component="th"
                scope="row"
                align={align}
                sx={{ Width: "20%" }}
              >
                {row.name}
              </StyledTableCell>
              <StyledTableCell align={align} sx={{ width: "20%" }}>
                {row.email}
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "9%" }}>
                {row.classesno}
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "9%" }}>
                {row.studentsno}
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "9%" }}>
                {row.sharePassword}
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "6%" }}>
                {row.password}
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "2%" }}>
                {row.edit}
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "2%" }}>
                {row.delete}
              </StyledTableCell>
            </StyledTableRow>
          )
        )}
      </TableBody>
    </>
  );
};

export default TBody;
